import React, { useEffect, useState } from 'react';
import { Text } from '@fluentui/react';
import { Marker, Popup, useMap } from 'react-leaflet';
import { divIcon, LatLng, LatLngBounds } from 'leaflet';
import { renderToStaticMarkup } from "react-dom/server";
import CarIconComponent from '../icons/car-icon/car-icon';

import ItemRuta from '../../models/ItemRuta';
import ItemRutaActuacion from '../../models/ItemRutaActuacion';

import 'leaflet';
import "leaflet-routing-machine";

declare let L : any;

const getLatLng = (value: string) : LatLng => {
    let valueArray : string[] = value.split(',');
    return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
};

const customIcon = (text: string, color: string) : HTMLElement => {
    let iconSpan: HTMLSpanElement = document.createElement("span");
    iconSpan.className = "d-inline-block";
    iconSpan.style.borderRadius = "50% 50% 50% 0";
    iconSpan.style.transform = "rotate(-45deg)";
    iconSpan.style.backgroundColor = color;
    iconSpan.style.height = "35px";
    iconSpan.style.width = "35px";
    iconSpan.style.padding = "3px";
    iconSpan.style.fontFamily = "DINNextRoundedLTProMediumRegular";
    
    let textSpan: HTMLSpanElement = document.createElement("span");
    textSpan.className = "bg-white text-center d-inline-block rounded rounded-circle";
    textSpan.style.color = color;
    textSpan.style.fontSize = "20px";
    textSpan.style.fontWeight = "bold";
    textSpan.style.transform = "rotate(45deg)";
    textSpan.style.height = "100%";
    textSpan.style.width = "100%";
    textSpan.innerText = text;

    iconSpan.append(textSpan);

    return iconSpan;
};

interface ILeafletRouteComponentProps {
    route: ItemRuta,
    evolution: boolean;
};

const LeafletRouteComponent = (props: ILeafletRouteComponentProps) : JSX.Element => {
    const map: L.Map = useMap();

    const [finishedDirections, setFinishedDirections] = useState<LatLng[]>();
    const [directions, setDirections] = useState<LatLng[]>();

    useEffect(() => {
        const getDirections = () : Promise<LatLng[]> => {
            return new Promise(async (resolve, reject) => {
                let waypoints : LatLng[] = [];

                if (props.route.SalidaPosicionGPS) {
                    waypoints.push(getLatLng(props.route.SalidaPosicionGPS));
                }
                else {
                    waypoints.push(getLatLng(props.route.Actuaciones[0].PosicionGPS));
                }

                for (let actuacion of props.route.Actuaciones) {
                    waypoints.push(getLatLng(actuacion.PosicionGPS));
                }

                if (props.route.IdaYVuelta) {
                    if (props.route.SalidaPosicionGPS) {
                        waypoints.push(getLatLng(props.route.SalidaPosicionGPS));
                    }
                    else {
                        waypoints.push(getLatLng(props.route.Actuaciones[0].PosicionGPS));
                    }
                }
                else {
                    waypoints.push(getLatLng(props.route.Actuaciones[props.route.Actuaciones.length - 1].PosicionGPS));
                }

                if (waypoints.length) {
                    resolve(waypoints);
                }
                else {
                    reject();
                }
            });
        };

        const getFinishedDirections = () : Promise<LatLng[]> => {
            return new Promise(async (resolve, reject) => {
                let waypoints : LatLng[] = [];

                if (props.route.SalidaPosicionGPS) {
                    waypoints.push(getLatLng(props.route.SalidaPosicionGPS));
                }
                else {
                    waypoints.push(getLatLng(props.route.Actuaciones[0].PosicionGPS));
                }

                for (let actuacion of props.route.Actuaciones) {
                    if (actuacion.Estado === 30) {
                        waypoints.push(getLatLng(actuacion.PosicionGPS));
                    }
                }

                if (!props.route.Actuaciones.some((actuacion: ItemRutaActuacion) => actuacion.Estado !== 30)) {
                    if (props.route.IdaYVuelta) {
                        if (props.route.SalidaPosicionGPS) {
                            waypoints.push(getLatLng(props.route.SalidaPosicionGPS));
                        }
                        else {
                            waypoints.push(getLatLng(props.route.Actuaciones[0].PosicionGPS));
                        }
                    }
                    else {
                        waypoints.push(getLatLng(props.route.Actuaciones[props.route.Actuaciones.length - 1].PosicionGPS));
                    }
                }
                else {
                    waypoints.push(waypoints.length ? waypoints[waypoints.length - 1] : waypoints[0]);
                }

                if (waypoints.length) {
                    resolve(waypoints);
                }
                else {
                    reject();
                }
            });
        };

        getDirections().then((directions: LatLng[]) => {
            setDirections(directions);
            if (props.evolution) {
                getFinishedDirections().then((directions: LatLng[]) => {
                    setFinishedDirections(directions);
                });
            }
        });
    }, [props.route, props.evolution]);

    useEffect(() => {
        if (map && directions && directions.length) {
            let routingControl: L.Control = L.Routing.control({
                waypoints: directions,
                lineOptions: {
                    styles: [{
                        color: props.route.Color ?? '#ed6d63',
                        opacity: 1,
                        weight: 5,
                    }],
                    addWaypoints: false,
                },
                createMarker: () => null,
            }).addTo(map);
    
            return () => {
                map.removeControl(routingControl);
            };
        }
    }, [map, directions]);

    useEffect(() => {
        if (map && finishedDirections && finishedDirections.length) {
            let finishedRoutingControl: L.Control = L.Routing.control({
                waypoints: finishedDirections,
                lineOptions: {
                    styles: [{
                        color: '#666666',
                        opacity: 1,
                        weight: 5,
                    }],
                    addWaypoints: false,
                },
                createMarker: () => null,
            }).addTo(map);
    
            return () => {
                map.removeControl(finishedRoutingControl);
            };
        }
    }, [map, finishedDirections]);

    useEffect(() => {
        if (map && directions && directions.length) {
            let bounds: LatLngBounds = new LatLngBounds(directions);

            if (finishedDirections && finishedDirections.length) {
                finishedDirections.forEach((point: LatLng) => {
                    bounds.extend(point);
                });
            }

            if (props.route.UltimaPosicionGPS) {
                bounds.extend(getLatLng(props.route.UltimaPosicionGPS));
            }

            if (bounds.isValid()) {
                setTimeout(() => {
                    map.fitBounds(bounds, {
                        padding: [50, 50],
                    });
                }, 2000);
            }
        }
    }, [map, props.route.UltimaPosicionGPS, directions, finishedDirections]);
    
    return (
        <>
            {
                props.route.SalidaPosicionGPS ? (
                    <Marker
                        position={getLatLng(props.route.SalidaPosicionGPS)}
                        icon={divIcon({
                            html: customIcon("S", "#000000"),
                        })}>
                        <Popup>
                            <Text variant="medium">
                                {props.route.SalidaDireccionNombre}
                            </Text>
                        </Popup>
                    </Marker>
                ) : (
                    null
                )
            }

            {
                props.route.Actuaciones.map((actuacion: ItemRutaActuacion, index: number) => (
                    <Marker 
                        key={index}
                        icon={divIcon({
                            html: customIcon(actuacion.Posicion.toString(), (props.evolution && actuacion.Estado === 30) ? "#666666" : props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63"),
                        })}
                        position={getLatLng(actuacion.PosicionGPS)}>
                        <Popup>
                            <>
                                <Text variant="medium">
                                    <strong>
                                        {actuacion.Referencia ? actuacion.Referencia : actuacion.IdTarea} - {actuacion.Nombre}
                                    </strong>
                                </Text>
                                <br/>
                                <Text>
                                    {actuacion.DireccionCompleta}
                                </Text>
                            </>
                        </Popup>
                    </Marker>
                ))
            }

            {
                props.route.UltimaPosicionGPS ? (
                    <Marker 
                        position={getLatLng(props.route.UltimaPosicionGPS)}
                        icon={divIcon({
                            html: renderToStaticMarkup(
                                <div style={{
                                    width: '35px',
                                    height: '35px',
                                    padding: "3px",
                                }}>
                                    <CarIconComponent color={props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63"}></CarIconComponent>
                                </div>
                            ),
                        })}>
                        <Popup>
                            <>
                                <Text variant="medium">
                                    <strong>
                                        {props.route.NombreUsuarioAsignado}
                                    </strong>
                                </Text>
                                <br/>
                                <Text>
                                    {props.route.UltimaPosicionGPS}
                                </Text>
                            </>
                        </Popup>
                    </Marker>
                ) : (
                    null
                )
            }
        </>
    );
};

export default LeafletRouteComponent;
